import { forwardRef, useImperativeHandle, useState, useMemo, useEffect } from 'react';
import { Search, Package } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useProperties } from '@/hooks/useProperties';
import { useOrganizations } from '@/hooks/useOrganizations';
import { useContracts } from '@/hooks/useContracts';
import OrganizationsMultiSelect from '@/components/v2/molecules/admin/tabs/AdminDashboardTab/OrganizationsMultiSelect';
import PropertiesMultiSelect from '@/components/v2/molecules/admin/tabs/AdminDashboardTab/PropertiesMultiSelect';
import PropertiesArticlesTable from '../tables/PropertiesArticlesTable';

interface PropertiesListViewProps {
  onViewArticles: (propertyId: string) => void;
}

export interface PropertiesListViewRef {
  openCreateModal: () => void;
}

const PropertiesListView = forwardRef<PropertiesListViewRef, PropertiesListViewProps>(({ onViewArticles }, ref) => {
  const [search, setSearch] = useState('');
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([]);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>([]);
  
  const propertiesQuery = useProperties();
  const organizationsQuery = useOrganizations();
  const contractsQuery = useContracts();
  
  const properties = propertiesQuery.data ?? [];
  const organizations = organizationsQuery.data ?? [];
  const contracts = contractsQuery.data ?? [];

  useImperativeHandle(ref, () => ({
    openCreateModal: () => {
      // No-op - properties are created elsewhere
    },
  }));

  // Properties filtered by organization (for the property selector)
  const propertiesForSelector = useMemo(() => {
    if (selectedOrganizations.length === 0) return properties;
    
    const activeContracts = contracts.filter(c => c.status === 'active');
    const validPropertyIds = new Set(
      activeContracts
        .filter(c => c.organization_id && selectedOrganizations.includes(c.organization_id))
        .map(c => c.property_id)
    );
    
    return properties.filter(p => validPropertyIds.has(p.id));
  }, [properties, contracts, selectedOrganizations]);

  // Sanitize selected properties when organization changes
  useEffect(() => {
    if (selectedOrganizations.length === 0) return;
    
    const validIds = new Set(propertiesForSelector.map(p => p.id));
    const sanitized = selectedPropertyIds.filter(id => validIds.has(id));
    
    if (sanitized.length !== selectedPropertyIds.length) {
      setSelectedPropertyIds(sanitized);
    }
  }, [propertiesForSelector, selectedPropertyIds, selectedOrganizations.length]);

  // Final filtered and sorted properties
  const filteredProperties = useMemo(() => {
    let result = properties;
    
    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(p => 
        p.title.toLowerCase().includes(searchLower) ||
        p.shortName?.toLowerCase().includes(searchLower)
      );
    }
    
    // Organization filter
    if (selectedOrganizations.length > 0) {
      const activeContracts = contracts.filter(c => c.status === 'active');
      const validPropertyIds = new Set(
        activeContracts
          .filter(c => c.organization_id && selectedOrganizations.includes(c.organization_id))
          .map(c => c.property_id)
      );
      result = result.filter(p => validPropertyIds.has(p.id));
    }
    
    // Specific properties filter
    if (selectedPropertyIds.length > 0) {
      result = result.filter(p => selectedPropertyIds.includes(p.id));
    }
    
    // Sort by sortOrder (same as calendar)
    return [...result].sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999));
  }, [properties, contracts, search, selectedOrganizations, selectedPropertyIds]);

  const isLoading = propertiesQuery.isLoading || organizationsQuery.isLoading || contractsQuery.isLoading;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar propiedad..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 rounded-full bg-muted/50 border-0"
          />
        </div>
        
        <OrganizationsMultiSelect
          organizations={organizations}
          selectedOrganizations={selectedOrganizations}
          onSelectionChange={setSelectedOrganizations}
        />
        
        <PropertiesMultiSelect
          properties={propertiesForSelector}
          selectedProperties={selectedPropertyIds}
          onSelectionChange={setSelectedPropertyIds}
        />
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      ) : filteredProperties.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No se encontraron propiedades</p>
        </div>
      ) : (
        <PropertiesArticlesTable
          properties={filteredProperties.map(p => ({
            id: p.id,
            title: p.title,
            short_name: p.shortName || null,
            images: p.images,
          }))}
          onViewArticles={onViewArticles}
        />
      )}
    </div>
  );
});

PropertiesListView.displayName = 'PropertiesListView';

export default PropertiesListView;
